/**
 * useDebouncedFamily — wraps pickDrawerStatusFamily with a short debounce so
 * the drawer's family-driven UI (sub-view selection, default expansion) does
 * not flicker when the status oscillates rapidly during reconnect / push-
 * vs-poll races.
 *
 * Critical: this debounces ONLY the family discriminant. Live field-level
 * data (pulse dot, current step, Progress field) is not routed through this
 * hook and continues to update immediately.
 *
 * Same-instance debounce: family changes for the same workflow instance are
 * held for ``debounceMs`` (default 1000ms). Transitions INTO the ``hitl``
 * family use the shorter ``hitlDebounceMs`` (default 500ms) because the user
 * needs to act on the HITL question quickly. Different-instance navigation
 * (Prev/Next click → new instanceId) flushes immediately so the user never
 * sees the previous workflow's family on the new instance.
 */

import { useEffect, useRef, useState } from "react";
import {
  pickDrawerStatusFamily,
  type DrawerStatusFamily,
} from "./pickDrawerStatusFamily";

export interface UseDebouncedFamilyOptions {
  debounceMs?: number;
  /** Debounce duration when the incoming family is ``"hitl"``. Shorter so the
   *  HITL question panel appears promptly after a SignalR push. */
  hitlDebounceMs?: number;
}

export interface DebouncedFamilyResult {
  /** The settled (debounced) family driving the current sub-view. */
  family: DrawerStatusFamily;
  /** True while a new target family is waiting for the debounce to settle. */
  isPending: boolean;
  /** The incoming family (what will render once the debounce fires). */
  targetFamily: DrawerStatusFamily;
}

export function useDebouncedFamily(
  status: string | null | undefined,
  hitlQuestion: unknown,
  instanceId: string | null | undefined,
  { debounceMs = 1000, hitlDebounceMs = 500 }: UseDebouncedFamilyOptions = {},
): DebouncedFamilyResult {
  const target = pickDrawerStatusFamily(status, hitlQuestion);
  const [debounced, setDebounced] = useState<DrawerStatusFamily>(target);
  const lastInstanceRef = useRef<string | null | undefined>(instanceId);

  useEffect(() => {
    if (instanceId !== lastInstanceRef.current) {
      // Instance navigation — flush immediately.
      lastInstanceRef.current = instanceId;
      setDebounced(target);
      return;
    }
    if (target === debounced) return;
    const delay = target === "hitl" ? hitlDebounceMs : debounceMs;
    const handle = setTimeout(() => setDebounced(target), delay);
    return () => clearTimeout(handle);
    // ``debounced`` is intentionally not a dep — including it would cancel
    // the in-flight debounce every time it settles, which is the opposite
    // of what we want.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, instanceId, debounceMs, hitlDebounceMs]);

  return { family: debounced, isPending: target !== debounced, targetFamily: target };
}
